import type { JourneyPayload, OuterPayload } from '../../../types/analyticstypes';
import { formatLatencyMs, formatMoney } from '../../../utils/analytics/format';

export default function CancelledBanner({
  journey,
  outer,
}: {
  journey: JourneyPayload;
  outer: OuterPayload;
}) {
  if (outer.status !== 'ABANDONED') return null;

  const events = journey.semanticEvents || [];
  const cancelEvent =
    [...events].reverse().find(
      (e) => e.type === 'session_cancel' || e.type === 'payment_cancelled' || e.type === 'session_timeout'
    ) || events[events.length - 1];
  const cancelMs = cancelEvent ? cancelEvent.ts - journey.sessionStartTs : journey.sessionDurationMs;
  const lastCart = journey.cart?.items || [];

  const navigateEvents = events.filter((e) => e.type === 'menu_navigate');

  return (
    <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <svg
          className="w-5 h-5 mt-0.5 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.34-2.69-1.34-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
          />
        </svg>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">
            Transaction cancelled
            {cancelEvent ? ` (${cancelEvent.type})` : ''} after {formatLatencyMs(cancelMs)}
          </p>
          {lastCart.length > 0 ? (
            <div className="mt-2 text-xs">
              <p className="font-medium mb-1">Last-known cart:</p>
              <ul className="space-y-0.5 ml-2">
                {lastCart.map((c, i) => (
                  <li key={i} className="flex justify-between gap-4">
                    <span>
                      {c.quantity}× {c.name}
                      {c.customizations && c.customizations.length > 0 && (
                        <span className="text-red-600/70"> — {c.customizations.join(', ')}</span>
                      )}
                    </span>
                    <span className="font-mono">{formatMoney(c.price)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="mt-1 text-xs">Cart was empty when the session ended.</p>
          )}
          {navigateEvents.length > 0 && (
            <p className="mt-2 text-xs">
              Browsed:{' '}
              <span className="font-medium">
                {navigateEvents
                  .map((e) => String(e.data?.category ?? ''))
                  .filter(Boolean)
                  .join(' → ') || '—'}
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
